import React, { useCallback, useMemo, Suspense } from 'react';
import { debounce } from 'lodash-es';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import {
  FlexibleTextContent,
  ButtonTreeNodeData,
  StreamingButtonTreeNodeData,
  FlexiblePageTreeNodeType,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import type { MentionItem } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsPlugin';
import {
  hydrateFlexibleTextVariables,
  normalizeStreamingFlexibleText,
  renderFlexibleTextToHtml,
} from '../../richtext/flexibleTextAst';
import { LexicalThemeColors } from '../../richtext/types';
import { cn } from '../../utils/cn';
import { resolveHref } from './resolveHref';
import { loadFlexibleTextInput } from '../../richtext/editorPreload';

const FlexibleTextInput = React.lazy(() => loadFlexibleTextInput().then((m) => ({ default: m.FlexibleTextInput })));

type ButtonProps = Omit<StreamingButtonTreeNodeData, 'type'> & {
  type?: FlexiblePageTreeNodeType.Button | FlexiblePageTreeNodeType.StructuredButton;
  id: string;
  children?: React.ReactNode;
  editorClassName?: string;
  onChange?: (id: string, data: Partial<ButtonTreeNodeData>) => void;
  mentionItems: MentionItem[];
  refetchMentionitems: () => Promise<void>;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor: boolean;
  onBlur?: () => void;
  onDoubleClick?: (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  isDoubleClickActive?: boolean;
  themeColors?: LexicalThemeColors;
};

type ButtonElementProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  commandfor?: string;
  command?: string;
  ['data-triggers-dialog']?: string;
};

export const Button = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      editorClassName,
      text,
      variant,
      children,
      id,
      onChange,
      mentionItems,
      refetchMentionitems,
      overrideVariables,
      href,
      isEditor,
      onBlur,
      onDoubleClick,
      isDoubleClickActive,
      themeColors,
      commandfor,
      command,
      type: _type,
      ...props
    },
    ref,
  ) => {
    const isEditing = isDoubleClickActive;

    // Normalize to FlexibleTextContent for safe processing (handles legacy RichText and streaming data)
    const normalizedText = useMemo(() => normalizeStreamingFlexibleText(text), [text]);

    // Generate variant class from data.variant, or check if it's already in className
    const variantClassName = useMemo(() => {
      if (variant === 'custom') {
        return undefined; // No variant class for custom
      }
      if (variant) {
        return `btn-${variant}`;
      }
      // Check if className already has a button variant class (backward compatibility)
      if (typeof className === 'string' && className.match(/btn-(primary|secondary|tertiary|outline|ghost|link)/)) {
        return undefined; // variant already in className, don't duplicate
      }
      return undefined;
    }, [variant, className]);

    // Transform className for container queries (editor preview).
    // Default to inline-flex centering so button text/children sit vertically
    // centered regardless of padding, height class, or font metrics. Text-only
    // buttons also get `w-fit` to hug content.
    const combinedClassName = children
      ? cn('inline-flex', className, variantClassName)
      : cn('inline-flex w-fit', className, variantClassName);

    const hydratedText = useMemo(() => {
      return hydrateFlexibleTextVariables(normalizedText, overrideVariables || {}, isEditor);
    }, [normalizedText, overrideVariables, isEditor]);

    const resolvedHref = useMemo(
      () => resolveHref(href, overrideVariables, isEditor),
      [href, overrideVariables, isEditor],
    );

    const renderedHtml = useMemo(() => {
      return renderFlexibleTextToHtml(hydratedText, undefined, { isEditor, mentionItems });
    }, [hydratedText, isEditor, mentionItems]);

    const handleDoubleClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        // Only trigger double-click for buttons with text property (not children)
        if (!children) {
          onDoubleClick?.(event);
        }
      },
      [onDoubleClick, children],
    );

    const handleBlur = useCallback(() => {
      onBlur?.();
    }, [onBlur]);

    const debouncedOnChange = useMemo(
      () =>
        debounce((newValue: FlexibleTextContent) => {
          onChange?.(id, { text: newValue });
        }, 300),
      [onChange, id],
    );

    const handleChange = useCallback(
      (newValue: FlexibleTextContent) => {
        debouncedOnChange(newValue);
      },
      [debouncedOnChange],
    );

    const content = useMemo(() => {
      if (!text && !children) return null;

      const textNode = text ? (
        isEditing ? (
          <span data-lexical-editor onDoubleClick={handleDoubleClick} style={{ cursor: 'text' }}>
            <Suspense fallback={<span dangerouslySetInnerHTML={{ __html: renderedHtml }} />}>
              <FlexibleTextInput
                value={normalizedText}
                onChange={handleChange}
                onBlur={handleBlur}
                editable
                enableTextAlign={false}
                enableFloatingMenu={false}
                enableExtendedTextFormatting
                variant={RichInputVariant.InlineEditing}
                autoFocus
                mentionItems={mentionItems}
                onMentionMenuOpen={refetchMentionitems}
                themeColors={themeColors}
              />
            </Suspense>
          </span>
        ) : (
          <span
            data-lexical-editor
            onDoubleClick={handleDoubleClick}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        )
      ) : null;

      return (
        <>
          {textNode}
          {children}
        </>
      );
    }, [
      children,
      text,
      isEditing,
      normalizedText,
      renderedHtml,
      mentionItems,
      handleDoubleClick,
      handleChange,
      handleBlur,
      refetchMentionitems,
      themeColors,
    ]);

    if (!href || (command && commandfor)) {
      const { openInNewTab, ...restProps } = props;
      const buttonProps: ButtonElementProps = {
        ...restProps,
        command,
        commandfor,
      };

      if (command === 'show-modal') {
        buttonProps['data-triggers-dialog'] = commandfor;
      }

      return (
        <button
          id={id}
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          className={cn(combinedClassName, editorClassName)}
          data-no-dnd={isEditing}
          style={{ cursor: isEditing ? 'text' : undefined }}
          onDoubleClick={handleDoubleClick}
          {...buttonProps}
        >
          {content}
        </button>
      );
    }

    const { openInNewTab, ...restProps } = props;

    const anchorProps = {
      ...restProps,
      href: !isEditor ? resolvedHref : undefined,
      target: openInNewTab ? '_blank' : '_self',
    };

    return (
      <a
        id={id}
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cn(combinedClassName, editorClassName)}
        data-no-dnd={isEditing}
        style={{ cursor: isEditing ? 'text' : undefined }}
        rel="noreferrer"
        onDoubleClick={handleDoubleClick}
        {...anchorProps}
      >
        {content}
      </a>
    );
  },
);

Button.displayName = 'Button';
